import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Spin, Select, Input, Switch, Tooltip ,message,Icon } from 'antd';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useArtboardContext, CONTEXT_KEYS } from '../../../context/complexArtboardContext';
import { PageElementsContext } from '../../../context/pageElementsContext';
import { useDocumentContext, COMPLEX_DOCUMENT_CONTEXT_KEYS } from '../../../context/complexDocumentContext';
import { correctBaseUrl } from '../../../utils/svgUtils';
import { cancellablePromise, useCancellablePromise } from '../cancellablePromise';
import { ImageView } from '../styles';
import HELPER from '../../../utils/helper';
import { BARCODE_TOOLBAR } from '../../../artboard/Toolbar/translationValues';
import { BARCODE_39 } from '../../../utils/complexDocumentConstants';
import { debounce } from 'underscore';
import { TEXT_DEBOUNCE_TIME_OUT } from '../../../utils/complexDocumentConstants';

const BASE_URL = HELPER.BASE_URL;
const { Option } = Select;
const NAMESPACES = ['BARCODE_TOOLBAR'];

const ReusableBarcodeEditor = (props) => {
  const { element, editable, selected } = props;
  const [url, setUrl] = useState(undefined);
  const [isVariable, toggleVariable] = useState(element.style.isVariable);

  const { t } = useTranslation(NAMESPACES);

  const customSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES], []);
  const [globalVariables] = useDocumentContext(customSelector, `Barcode-${element.uuid}`);

  const dataSetSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.DATA_SET], []);
  const [dataSet] = useDocumentContext(dataSetSelector, `CreativeBarCodeBlock-${element.uuid}`);

  const previewModeSelector = useCallback(store => store[CONTEXT_KEYS.ENABLE_PAGE_OVERFLOW], []);
  const [isPreview] = useArtboardContext(previewModeSelector,  `CreativeBarcodeBlock-${element.uuid}`);

  const interactiveDesignSelector = useCallback(store => store[CONTEXT_KEYS.IS_INTERACTIVE_DESIGN] || store[CONTEXT_KEYS.IS_OPEN_DESIGN], []);
  const [isInteractiveDesign] = useArtboardContext(interactiveDesignSelector, `ComplexArtboard`);

  const [elementValue, setElementValue] = useState(element.content);
  const [invalidValue, setInvalidValue] = useState(false);

  const { updateComplexElementDetails } = useContext(PageElementsContext);

  const { addCancellablePromise, removePendingPromise, clearPendingPromises } = useCancellablePromise();

  useEffect(() => {
    let value = element.content;
    if (element.style.isVariable && !isInteractiveDesign) {
      if (isPreview) {
        const variableName = globalVariables.find(variable => variable.id === Number(value))
          ?.placeHolderText;
        if (!!element.repeatBlockName && element.repeatBlockIndex !== undefined) {
          value = dataSet[element.repeatBlockName][element.repeatBlockIndex][variableName];
        } else {
          value = dataSet[variableName];
        }
      } else {
        value = globalVariables.find(variable => variable.id === Number(value))?.exampleValue;
      }
    }
    const newUrl = `${BASE_URL}barcode?type=${element.barcodeType}&text=${
                         encodeURIComponent(value)}&width=${parseInt(
                         `${element.style.width}`.replaceAll('px', ''), 10)}&height=${parseInt(
                         `${element.style.height}`.replaceAll('px', ''))}`;
    setUrl(newUrl);
  }, [element, globalVariables]);

  function isValidString(input) {
    const regex = /^[0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ\-.\s*$/+%]+$/;
    return regex.test(input);
  }

  const onSelect = useCallback(({ key }) => {
    message.destroy()
    const variableName = globalVariables.find(gVar => gVar.id === key)?.exampleValue;
    if (!isValidString(variableName) && element.barcodeType === BARCODE_39) {
      setInvalidValue(true);
      handleBarcodeError();
      return;
    } else {
      setInvalidValue(false);
    }
    updateComplexElementDetails(element.uuid, {
      style: { isVariable: true },
      content: `${key}`
    }, 'barcode-value');
    // TODO : Add this variable to the document variables context???
  }, []);


  const onDefaultValueChange = useCallback(async e => {
    const value = e.target.value;
    setElementValue(value);
    debounce(debounceUpdateValue(value), TEXT_DEBOUNCE_TIME_OUT);
  }, []);

  const handleBarcodeError = () => {
    message.config({ top: 80 });
    message.error(t(BARCODE_TOOLBAR.INVALID_SELECTED_VALUE_ERROR_MESSAGE.key, BARCODE_TOOLBAR.INVALID_SELECTED_VALUE_ERROR_MESSAGE.value));
    message.config({ top: 24 });
  };

  const debounceUpdateValue = async value => {
    clearPendingPromises();
    const cancellablePromise1 = cancellablePromise(
      new Promise(resolve =>
        setTimeout(() => {
          resolve(value);
        }, 1000),
      ),
    );
    addCancellablePromise(cancellablePromise1);
    if (element.barcodeType === BARCODE_39 && !isValidString(value) && value.length) {
      setInvalidValue(true);
      return;
    } else {
      setInvalidValue(false);
    };

    if (value.length === 0) {
      return;
    }

    try {
      const resolvedValue = await cancellablePromise1.promise;
      updateComplexElementDetails(element.uuid, {
        style: { isVariable: false },
        content: resolvedValue
      }, 'barcode-value');
    } catch (error) {
      removePendingPromise(cancellablePromise1);
    }
  };

  const onToggleChange = value => {
    toggleVariable(value);
    setInvalidValue(false);
    if (!value) {
      const name =
        element.barcodeType === BARCODE_39
          ? `SAMPLE ${element.barcodeType.toUpperCase()} BARCODE`
          : `sample ${element.barcodeType} barcode`;
          setElementValue(name); 

      updateComplexElementDetails(
        element.uuid,
        {
          style: { isVariable: false },
          content: name,
        },
        'barcode-value',
      );
    }
  };

  if (!url) {
    return <Spin />
  }

  let content = element.content;
  let variableName;
  if (element.style.isVariable) {
    content = Number(element.content);
    variableName = globalVariables.find(gVar => gVar.id === content)?.placeHolderText;
  }

  return (
    <>
      <ImageView
        id={`img-${element.uuid}`}
        src={correctBaseUrl(url)}
        alt="droppable-image"
      />
      {editable && selected && ReactDOM.createPortal(
        (
        <div id={`toolbar-${element.uuid}`} onKeyDown={e => e.stopPropagation()} style={{ padding: '4px 0px' }}>
          <Tooltip
            placement="bottom"
            title={t(BARCODE_TOOLBAR.SWITCH_BARCODE_VARIABLE.key, BARCODE_TOOLBAR.SWITCH_BARCODE_VARIABLE.value)}
          >
            <Switch checked={isVariable} onChange={onToggleChange} style={{ marginRight: 10 }} disabled={isInteractiveDesign} />
          </Tooltip>
          <div style={{ color: 'white', display: 'inline-block', marginRight: 10 }}>
            {isVariable
              ? t(BARCODE_TOOLBAR.VARIABLE_LABEL.key, BARCODE_TOOLBAR.VARIABLE_LABEL.value)
              : t(BARCODE_TOOLBAR.BARCODE_TEXT_PLACEHOLDER.key, BARCODE_TOOLBAR.BARCODE_TEXT_PLACEHOLDER.value)
            }
          </div>
          {isVariable && <Select
            showSearch
            style={{ width: 150, marginRight: 10, border :  invalidValue && " 2px solid red" , borderRadius:"5px" }}
            placeholder={t(BARCODE_TOOLBAR.BARCODE_VARIABLE_DROPDOWN_PLACEHOLDER.key, BARCODE_TOOLBAR.BARCODE_VARIABLE_DROPDOWN_PLACEHOLDER.value)}
            optionFilterProp="children"
            value={variableName ? { key: content, label: variableName } : undefined}
            labelInValue
            onSelect={onSelect}
            filterOption={(input, option) =>
              option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {globalVariables.map((data, index) => (
              (data.type !== "ListString"|| data.type !== 'ListDate') &&
              <Option key={data.id + index} value={data.id}>
                {data.placeHolderText}
              </Option>
            ))}
          </Select>}
          {!isVariable && <Input
            name="barcodeText"
            type='text'
            placeholder={t(BARCODE_TOOLBAR.BARCODE_TEXT_PLACEHOLDER.key, BARCODE_TOOLBAR.BARCODE_TEXT_PLACEHOLDER.value)}
            style={{ width: 150, marginRight: 5, border :  invalidValue && " 2px solid red" , borderRadius:"5px" }}
            value={elementValue}
            onChange={onDefaultValueChange}
            autoComplete={"off"}
                suffix={
                  element.barcodeType === BARCODE_39 ? (
                    <Tooltip
                      placement="bottom"
                      title={t(BARCODE_TOOLBAR.BARCODE_39_PERMITTED_CHARACTER.key, BARCODE_TOOLBAR.BARCODE_39_PERMITTED_CHARACTER.value)}
                    >
                      <Icon type="info-circle" style={{ marginLeft: '5px' }} />
                    </Tooltip>
                  ) : (
                    ''
                  )
                }
              />
            }
          </div>),
        document.getElementById('document-editor-toolbar'),
      )}
    </>
  );
}

export default ReusableBarcodeEditor;